import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { Plus, Search, ShoppingCart, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, X, FileText } from 'lucide-react';
import { useServerDrafts } from '../hooks/useServerDrafts';
import { DraftsPanel } from '../components/DraftsPanel';

const PAGE_SIZE = 50;

const SalesOrders = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // R13: Drafts tab — managed by useServerDrafts. Active tab persists in URL so refresh keeps you on the right view.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'drafts' ? 'drafts' : 'orders');
  const { drafts: serverDrafts, deleteDraft } = useServerDrafts('sales_order');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [leaseFilter, setLeaseFilter] = useState(searchParams.get('lease') || '');
  const [leaseInput, setLeaseInput] = useState(searchParams.get('lease') || '');
  const [leaseSuggestions, setLeaseSuggestions] = useState([]);
  const [showLeaseDrop, setShowLeaseDrop] = useState(false);
  const [customerFilter, setCustomerFilter] = useState(searchParams.get('customer') || '');
  const [sortField, setSortField] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const leaseRef = useRef(null);
  const leaseTimeout = useRef(null);

  // Sync filters to URL params so back navigation preserves them
  useEffect(() => {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (leaseFilter) params.lease = leaseFilter;
    if (customerFilter) params.customer = customerFilter;
    if (activeTab === 'drafts') params.tab = 'drafts';
    setSearchParams(params, { replace: true });
  }, [searchQuery, leaseFilter, customerFilter, activeTab]); // eslint-disable-line

  // Reset to page 1 whenever search/filter changes
  useEffect(() => { setCurrentPage(1); }, [leaseFilter, customerFilter, searchQuery]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const params = { skip: (currentPage - 1) * PAGE_SIZE, limit: PAGE_SIZE };
        if (leaseFilter.trim()) params.lease_name = leaseFilter.trim();
        if (customerFilter.trim()) params.customer_name = customerFilter.trim();
        if (searchQuery.trim()) params.search = searchQuery.trim();
        const data = await api.getSalesOrders(params);
        setOrders(data);
      } catch (err) {
        console.error('Failed to load orders', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [leaseFilter, customerFilter, searchQuery, currentPage]);

  // Load total count for pagination
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const params = new URLSearchParams();
        if (leaseFilter.trim()) params.append('lease_name', leaseFilter.trim());
        if (customerFilter.trim()) params.append('customer_name', customerFilter.trim());
        if (searchQuery.trim()) params.append('search', searchQuery.trim());
        const res = await api.request(`/api/sales-orders/count?${params.toString()}`);
        setTotalCount(res?.total || 0);
      } catch (e) { console.error('Failed to load count', e); }
    };
    fetchCount();
  }, [leaseFilter, customerFilter, searchQuery]);

  // Close lease dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (leaseRef.current && !leaseRef.current.contains(e.target)) setShowLeaseDrop(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLeaseInput = useCallback((val) => {
    setLeaseInput(val);
    if (leaseTimeout.current) clearTimeout(leaseTimeout.current);
    if (!val.trim()) {
      setLeaseSuggestions([]);
      setShowLeaseDrop(false);
      setLeaseFilter('');
      setCurrentPage(1);
      return;
    }
    leaseTimeout.current = setTimeout(async () => {
      try {
        const names = await api.getLeaseNames(val.trim());
        setLeaseSuggestions(names);
        setShowLeaseDrop(names.length > 0);
      } catch { setLeaseSuggestions([]); }
    }, 250);
  }, []);

  const selectLease = (name) => {
    setLeaseInput(name);
    setLeaseFilter(name);
    setShowLeaseDrop(false);
    setCurrentPage(1);
  };

  const clearLease = () => {
    setLeaseInput('');
    setLeaseFilter('');
    setLeaseSuggestions([]);
    setShowLeaseDrop(false);
    setCurrentPage(1);
  };

  const filtered = searchQuery
    ? orders.filter(o =>
        (o.order_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.contact_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.billing_city || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.sales_rep || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (o.lease_name || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : orders;

  const sorted = [...filtered].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    // Date fields: parse to timestamps for proper comparison
    if (sortField === 'order_date' || sortField === 'created_at' || sortField === 'due_date') {
      const aTime = aVal ? new Date(aVal).getTime() : 0;
      const bTime = bVal ? new Date(bVal).getTime() : 0;
      return sortDir === 'asc' ? aTime - bTime : bTime - aTime;
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  const statusBadge = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      draft: 'bg-gray-100 text-gray-600',
      cancelled: 'bg-red-100 text-red-700',
      quote: 'bg-purple-100 text-purple-700',
    };
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  const paymentBadge = (order) => {
    const status = order.payment_status;
    const isPaid = status === 'completed' || status === 'paid';
    const isPartial = status === 'partial';
    const colors = {
      completed: 'bg-green-100 text-green-700',
      paid: 'bg-green-100 text-green-700',
      partial: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
    };
    return (
      <div className="flex flex-col gap-0.5">
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full inline-block w-fit ${colors[status] || 'bg-gray-100'}`} data-testid={`payment-badge-${order.id}`}>
          {isPaid ? 'Paid' : isPartial ? 'Partial' : 'Unpaid'}
        </span>
        {/* Return status badge — shown in red below payment status */}
        {order.return_status === 'fully_returned' && (
          <span className="px-2 py-0.5 text-xs font-bold rounded-full inline-block w-fit bg-red-100 text-red-700 border border-red-300" data-testid={`return-badge-${order.id}`}>
            Returned
          </span>
        )}
        {order.return_status === 'partially_returned' && (
          <span className="px-2 py-0.5 text-xs font-bold rounded-full inline-block w-fit bg-red-100 text-red-700 border border-red-300" data-testid={`return-badge-${order.id}`}>
            Partially Returned
          </span>
        )}
        {isPaid && order.payment_method && (
          <span className="text-[10px] text-muted-foreground capitalize">{order.payment_method === 'square' ? 'Square' : order.payment_method}</span>
        )}
      </div>
    );
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';
  const fmtAddr = (order) => [order.billing_city, order.billing_state].filter(Boolean).join(', ');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const paginatedOrders = sorted; // server-paginated

  return (
    <div className="space-y-6" data-testid="sales-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Sales Orders</h2>
          <p className="text-muted-foreground">{totalCount.toLocaleString()} orders{searchQuery && ' (filtered)'}</p>
        </div>
        <Link to="/sales-orders/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          data-testid="new-sales-order-btn">
          <Plus className="h-4 w-4" /> New Order
        </Link>
      </div>

      {/* Tab navigation: All Orders | Drafts (R13) */}
      <div className="border-b flex items-center gap-1" data-testid="so-tabs">
        <button
          type="button"
          onClick={() => setActiveTab('orders')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'orders' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-orders"
        >
          <ShoppingCart className="h-4 w-4 inline mr-1.5" /> All Orders
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('drafts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'drafts' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="tab-drafts"
        >
          <FileText className="h-4 w-4 inline mr-1.5" /> Drafts
          {serverDrafts.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full min-w-[1.25rem]">{serverDrafts.length}</span>
          )}
        </button>
      </div>

      {activeTab === 'drafts' ? (
        /* Drafts tab — full-width Saved Drafts list. Resume opens /sales-orders/new?draft=<id>. */
        <div className="bg-card border rounded-xl p-6" data-testid="drafts-tab-content">
          {serverDrafts.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No saved drafts. Drafts you start on the New Order page will appear here.</p>
            </div>
          ) : (
            <DraftsPanel
              drafts={serverDrafts}
              onResume={(d) => navigate(`/sales-orders/new?draft=${d.id}`)}
              onDelete={deleteDraft}
              title={`Sales Order Drafts (${serverDrafts.length})`}
            />
          )}
        </div>
      ) : (
      <>
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" placeholder="Search by order #, customer, lease name, city, sales rep..."
            value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full pl-9 pr-4 py-2 border border-input rounded-md" data-testid="so-search" />
        </div>
        <div className="relative max-w-xs" ref={leaseRef}>
          <input type="text" placeholder="Filter by Lease Name..."
            value={leaseInput} onChange={(e) => handleLeaseInput(e.target.value)}
            onFocus={() => { if (leaseSuggestions.length > 0) setShowLeaseDrop(true); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setLeaseFilter(leaseInput); setShowLeaseDrop(false); setCurrentPage(1); } }}
            className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="lease-name-filter" />
          {leaseInput && (
            <button onClick={clearLease}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" data-testid="clear-lease-filter">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {showLeaseDrop && leaseSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 max-h-56 overflow-y-auto" data-testid="lease-suggestions">
              {leaseSuggestions.map((name, i) => (
                <button key={i} onClick={() => selectLease(name)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors truncate"
                  data-testid={`lease-suggestion-${i}`}>
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative max-w-xs">
          <input type="text" placeholder="Filter by Customer..."
            value={customerFilter} onChange={(e) => { setCustomerFilter(e.target.value); setCurrentPage(1); }}
            className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="customer-name-filter" />
          {customerFilter && (
            <button onClick={() => setCustomerFilter('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" data-testid="clear-customer-filter">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('order_number')}>
                  Order # <SortIcon field="order_number" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('order_date')}>
                  Date <SortIcon field="order_date" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('customer_name')}>
                  Customer <SortIcon field="customer_name" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Location</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('lease_name')}>
                  Lease Name <SortIcon field="lease_name" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Payment</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Sales Rep</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Items</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase cursor-pointer select-none" onClick={() => toggleSort('total')}>
                  Total <SortIcon field="total" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="10" className="px-4 py-8 text-center text-muted-foreground">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No sales orders found
                </td></tr>
              ) : (
                paginatedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/sales-orders/${order.id}`)} data-testid={`so-row-${order.id}`}>
                    <td className="px-4 py-3 font-medium font-mono text-sm">{order.order_number}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {fmtDate(order.order_date || order.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <p className="font-medium">{order.customer_name || '\u2014'}</p>
                      {order.contact_name && <p className="text-xs text-muted-foreground">{order.contact_name}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{fmtAddr(order) || '\u2014'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground" data-testid={`lease-name-cell-${order.id}`}>{order.lease_name || '\u2014'}</td>
                    <td className="px-4 py-3">{statusBadge(order.status)}</td>
                    <td className="px-4 py-3">{paymentBadge(order)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{order.sales_rep || '\u2014'}</td>
                    <td className="px-4 py-3 text-sm text-right">{(order.line_items || []).reduce((sum, li) => sum + (Number(li.quantity) || 0), 0)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium tabular-nums">
                      ${(order.total || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/30" data-testid="so-pagination">
            <p className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} orders
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                className="px-2.5 py-1.5 text-xs border border-input rounded-md hover:bg-muted disabled:opacity-40">First</button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-1.5 border border-input rounded-md hover:bg-muted disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 7) page = i + 1;
                else if (currentPage <= 4) page = i + 1;
                else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                else page = currentPage - 3 + i;
                return (
                  <button key={page} onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      currentPage === page ? 'bg-primary text-white border-primary' : 'border-input hover:bg-muted'
                    }`}>{page}</button>
                );
              })}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-1.5 border border-input rounded-md hover:bg-muted disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 text-xs border border-input rounded-md hover:bg-muted disabled:opacity-40">Last</button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default SalesOrders;
